import React, { useMemo, useLayoutEffect } from 'react';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { stringify } from 'query-string';

import getFiltersFromUrl from 'helpers/getFiltersFromUrl';

import {
  saveTopicFilters,
  saveTopicFiltersRemote,
} from 'redux/reducers/topicFilters';
import {
  ITopicFilterState,
  validFiltersNames,
} from 'components/TopicFilters/shared';

/**
 * Sources and order of precedence of search parameters:
 * 1. A link or bookmark copied into the browser address bar
 * 2. The key/value store where topic filters are stored
 * 3. Defaults from the store's initial state
 *
 * if the the filters in the redux store are different from the filters in url,
 * the store will be updated with the url values.
 */

interface IProps {
  topicFiltersState: ITopicFilterState;
}

/**
 * Adds filter parameters to the URL of Entity and Entity compare pages
 * When the Url filter parameters differ from the current store values,
 * the store is updated with the Url values.
 */
const SyncTopicFiltersStoreWithURL: React.FC<IProps> = (props) => {
  const { topicFiltersState } = props;

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const params = useMemo(() => qs.parse(location.search), [location.search]);
  const filtersInUrl = useMemo(() => getFiltersFromUrl(location), [location]);

  useLayoutEffect(() => {
    const topicFilterValidState = Object.entries(topicFiltersState).reduce(
      (acc: any, [key, value]) => {
        if (value && validFiltersNames.includes(key)) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );
    const areStateAndUrlFiltersEqual = isEqual(
      filtersInUrl,
      topicFilterValidState
    );

    // the query param should only be preserved in the search screen
    if (!location.pathname.startsWith('/search') && 'query' in params) {
      delete params.query;
    }

    // A non-empty url with filter params has been copy/pasted, so
    // we persist the filter values taken from the url
    if (!areStateAndUrlFiltersEqual && Object.keys(filtersInUrl).length) {
      dispatch(saveTopicFilters(filtersInUrl));
      dispatch(saveTopicFiltersRemote(filtersInUrl));
      history.replace({
        search: stringify(params),
      });
    }

    // The url bar is empty and the persisted filters contain values,
    // so we update the url with the persisted filters.
    // (This happens when the browser is refreshed with an empty url bar or when logging in)
    else if (
      !areStateAndUrlFiltersEqual &&
      Object.keys(topicFilterValidState).length
    ) {
      history.replace({ search: stringify(topicFilterValidState) });
    }
  }, [
    dispatch,
    filtersInUrl,
    topicFiltersState,
    history,
    params,
    location.pathname,
  ]);

  return null;
};

export default SyncTopicFiltersStoreWithURL;
